import React from 'react';
import { cn } from '@/utils/cn';
import { TeeTableSkeleton } from '@/components/ui/tee-table-skeleton';
import { useIsMobile } from '@/hooks/useBreakpoint';
import {
  IconChevronDown as ChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconArrowUp,
  IconArrowDown,
  IconArrowsSort,
} from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Pagination } from '@/types/pagination';
import { TeeButton } from './tee-button';

export interface Column<T> {
  key: keyof T;
  label: string;
  /** Short explanation shown from an info icon next to the header label */
  labelTooltip?: string;
  /**
   * CSS grid track, e.g. `'1.4fr'`. When any column sets one, the table lays
   * out exactly its own columns instead of auto-fit, which otherwise leaves
   * empty tracks after the last column.
   */
  width?: string;
  render?: (value: any, row: T, index?: number) => React.ReactNode;
  /** When set, the desktop header label becomes a clickable sort toggle. */
  onSort?: () => void;
  /** Current sort direction for this column; drives aria-sort and the arrow icon. */
  sortDirection?: 'asc' | 'desc' | null;
}

export interface TeeTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pagination?: Pagination;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
  getRowClassName?: (row: T, index: number) => string | undefined;
  /** Provide a stable key for each row to avoid React reconciliation issues on sort/filter. */
  rowKey?: (row: T, index: number) => string | number;

  /**
   * Render detail underneath a row, revealed in place.
   *
   * When supplied, rows become clickable and a chevron is added. Preferred over
   * a side drawer when the detail belongs to the row rather than replacing it —
   * the reader keeps the surrounding rows for context and never loses their
   * place in the list. Return `null` for rows that have nothing to expand.
   */
  renderExpanded?: (row: T, index: number) => React.ReactNode;

  /** Keys of the rows currently open. Controlled by the caller. */
  expandedKeys?: Set<string | number>;

  /** Called with a row's key when it is clicked. */
  onToggleExpand?: (key: string | number, row: T, index: number) => void;

  /**
   * Tighter padding/font-size for dense, many-column data (e.g. shot-by-shot
   * TrackMan readings) — desktop grid only, opt-in, defaults to false so
   * every existing consumer's sizing is unaffected.
   */
  compact?: boolean;
}

function getPageWindow(
  current: number,
  total: number,
  delta = 2
): (number | 'ellipsis-start' | 'ellipsis-end')[] {
  if (total <= 1) return total === 1 ? [1] : [];

  const range = new Set<number>();
  range.add(1);
  range.add(total);
  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i++
  ) {
    range.add(i);
  }

  const sorted = Array.from(range).sort((a, b) => a - b);
  const result: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push(i === 1 ? 'ellipsis-start' : 'ellipsis-end');
    }
    result.push(sorted[i]);
  }

  return result;
}

export const TeeTable = React.forwardRef<HTMLDivElement, TeeTableProps<any>>(
  (
    {
      columns,
      data,
      loading = false,
      pagination,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions = [20, 50, 100],
      className,
      getRowClassName,
      rowKey,
      renderExpanded,
      expandedKeys,
      onToggleExpand,
      compact = false,
    },
    ref
  ) => {
    // Card vs table is a DOM-shape change, not a reflow — see the comment at
    // the render branch below.
    const isMobile = useIsMobile();

    const minTrack = `minmax(${compact ? 72 : 100}px, 1fr)`;
    const trackTemplate = columns.some((column) => column.width)
      ? columns.map((column) => column.width ?? minTrack).join(' ')
      : `repeat(auto-fit, ${minTrack})`;

    const handlePrevious = () => {
      if (pagination && pagination.page > 1 && onPageChange) {
        onPageChange(pagination.page - 1);
      }
    };

    const handleNext = () => {
      if (
        pagination &&
        pagination.page < pagination.totalPages &&
        onPageChange
      ) {
        onPageChange(pagination.page + 1);
      }
    };

    return (
      <div ref={ref} className={cn('flex flex-col gap-3', className)}>
        {/* `loading` is checked before emptiness. The prop was declared but
            never read, so every table flashed "No data available" on the way
            to its first render — an empty result and a pending one looked
            identical, which reads as "nothing here" rather than "wait". */}
        {loading ? (
          /* A skeleton rather than a spinner: it occupies the same shape the
             rows will, so the table does not resize when data lands, and it
             reads as "content is coming" instead of "something is happening
             somewhere". Column count follows the real columns so the widths
             line up. */
          <TeeTableSkeleton cols={Math.max(columns.length, 1)} rows={6} />
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center px-4 py-12 bg-[var(--background-card)] rounded-[10px] border border-[var(--border-default)]">
            <p className="text-body-m text-[var(--text-content-sub-title,#6C756D)]">
              No data available
            </p>
          </div>
        ) : (
          <>
            {/* Desktop: the original grid. Mobile: one card per row, built
                from a different DOM shape rather than reflowed with CSS —
                "identifier + actions on a header line, everything else in two
                columns" cannot be expressed with flex-wrap alone, because
                `ml-auto` only pushes to the end of its own wrapped line. */}
            {isMobile ? (
              <div className="flex flex-col gap-3">
                {data.map((row, idx) => {
                  // Columns with no header label are action columns
                  // (TeeActionsMenu et al) — they belong in the header, not
                  // as a field.
                  // A whitespace-only label counts as unlabelled too — from
                  // main, where callers pass `label: ' '` to keep a blank
                  // desktop header cell.
                  const isActionColumn = (c: Column<any>) =>
                    !c.label || c.label.trim() === '';
                  const actionColumns = columns.filter(isActionColumn);
                  const fieldColumns = columns.filter((c) => !isActionColumn(c));
                  const [identifier, ...rest] = fieldColumns;
                  return (
                    <div
                      key={rowKey ? rowKey(row, idx) : idx}
                      data-testid="tee-table-row"
                      className={cn(
                        'flex flex-col rounded-[12px] border border-[var(--border-default)] bg-[var(--background-card)] overflow-hidden',
                        'shadow-[0px_1px_2px_0px_rgba(12,61,45,0.04)]',
                        getRowClassName?.(row, idx)
                      )}
                    >
                      {/* Header — identifier left, actions pinned right */}
                      {identifier && (
                        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[var(--background-page-header)] border-b border-[var(--border-default)]">
                          <div className="min-w-0 flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-content-sub-title)]">
                              {identifier.label}
                            </span>
                            <span className="text-body-m font-semibold text-[var(--text-content-title)] truncate">
                              {identifier.render
                                ? identifier.render(row[identifier.key], row, idx)
                                : String(row[identifier.key] ?? '-')}
                            </span>
                          </div>
                          {actionColumns.length > 0 && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {actionColumns.map((column) => (
                                <div key={String(column.key)}>
                                  {column.render
                                    ? column.render(row[column.key], row, idx)
                                    : String(row[column.key] ?? '')}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Body — two columns of label-above-value */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
                        {rest.map((column) => (
                          <div key={String(column.key)} className="min-w-0 flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-content-sub-title)]">
                              {column.label}
                            </span>
                            <div className="text-body-s font-medium text-[hsl(var(--foreground))] break-words">
                              {column.render
                                ? column.render(row[column.key], row, idx)
                                : String(row[column.key] ?? '-')}
                            </div>
                          </div>
                        ))}
                      </div>

                      {(() => {
                        const key = rowKey ? rowKey(row, idx) : idx;
                        const detail = renderExpanded?.(row, idx);
                        if (!detail) return null;
                        const isOpen = Boolean(expandedKeys?.has(key));

                        return (
                          <>
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              onClick={() => onToggleExpand?.(key, row, idx)}
                              className="flex items-center justify-center gap-1 px-4 py-2 border-t border-[var(--border-default)] text-[12px] font-medium text-[var(--color-forest)]"
                            >
                              {isOpen ? 'Hide details' : 'Details'}
                              <ChevronDown
                                size={14}
                                aria-hidden="true"
                                className={cn(
                                  'transition-transform duration-200',
                                  isOpen && 'rotate-180'
                                )}
                              />
                            </button>
                            {isOpen && (
                              <div className="px-4 pb-4 pt-1 bg-[var(--background-card-elevated)] border-t border-[var(--border-default)]">
                                {detail}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid gap-3">
                <div
                  className={cn(
                    'grid bg-[var(--color-forest)] rounded-t-[10px] border border-[var(--color-forest-deep)]',
                    compact ? 'gap-3 px-3 py-2' : 'gap-4 px-4 py-3'
                  )}
                  // The chevron gutter must be declared here too, or every
                  // header cell sits one track out of step with its column.
                  style={{
                    gridTemplateColumns: `${trackTemplate}${renderExpanded ? ' 28px' : ''}`,
                  }}
                >
                  {columns.map((column) => {
                    const labelContent = column.labelTooltip ? (
                      <span className="inline-flex items-center gap-1">
                        {column.label}
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} aria-label={column.labelTooltip} className="inline-flex cursor-help">
                                <IconInfoCircle size={13} aria-hidden="true" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-[260px] whitespace-normal normal-case tracking-normal font-normal text-body-xs text-[var(--text-content-title)]"
                            >
                              {column.labelTooltip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </span>
                    ) : (
                      column.label
                    );

                    const ariaSort =
                      column.sortDirection === 'asc'
                        ? 'ascending'
                        : column.sortDirection === 'desc'
                          ? 'descending'
                          : 'none';

                    return (
                      <div
                        key={String(column.key)}
                        className={cn(
                          'text-left font-semibold text-[var(--text-on-forest)] truncate uppercase tracking-wider',
                          compact ? 'text-[10px]' : 'text-[11px]'
                        )}
                        {...(column.onSort ? { 'aria-sort': ariaSort as React.AriaAttributes['aria-sort'] } : {})}
                      >
                        {column.onSort ? (
                          <button
                            type="button"
                            onClick={column.onSort}
                            className="inline-flex items-center gap-1 uppercase tracking-wider text-inherit"
                          >
                            {labelContent}
                            {column.sortDirection === 'asc' ? (
                              <IconArrowUp size={12} aria-hidden="true" />
                            ) : column.sortDirection === 'desc' ? (
                              <IconArrowDown size={12} aria-hidden="true" />
                            ) : (
                              <IconArrowsSort size={12} aria-hidden="true" className="opacity-50" />
                            )}
                          </button>
                        ) : (
                          labelContent
                        )}
                      </div>
                    );
                  })}
                  {renderExpanded && <div aria-hidden="true" />}
                </div>

                {data.map((row, idx) => {
                  const key = rowKey ? rowKey(row, idx) : idx;
                  const detail = renderExpanded?.(row, idx);
                  const isOpen = Boolean(expandedKeys?.has(key));

                  return (
                    <div key={key} className="flex flex-col">
                      <div
                        data-testid="tee-table-row"
                        // Only interactive when there is something to reveal —
                        // a cursor and an aria-expanded on an inert row is a
                        // promise the table cannot keep.
                        {...(detail
                          ? {
                              role: 'button' as const,
                              tabIndex: 0,
                              'aria-expanded': isOpen,
                              onClick: () => onToggleExpand?.(key, row, idx),
                              onKeyDown: (e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onToggleExpand?.(key, row, idx);
                                }
                              },
                            }
                          : {})}
                        className={cn(
                          'grid bg-[var(--background-card)] border-b border-[var(--border-default)] hover:bg-[var(--background-card-elevated)] transition-colors',
                          compact ? 'gap-3 px-3 py-2' : 'gap-4 px-4 py-3',
                          detail && 'cursor-pointer',
                          isOpen && 'bg-[var(--background-card-elevated)] border-b-0',
                          getRowClassName?.(row, idx)
                        )}
                        style={{
                          gridTemplateColumns: `${trackTemplate}${detail ? ' 28px' : ''}`,
                        }}
                      >
                        {columns.map((column) => (
                          <div
                            key={String(column.key)}
                            className={cn(
                              'flex flex-col gap-1 text-[hsl(var(--foreground))] min-w-0',
                              compact ? 'text-[13px]' : 'text-body-s'
                            )}
                          >
                            <div
                              className={cn(
                                'font-medium truncate',
                                compact ? 'leading-[18px]' : 'leading-[var(--line-height-body-s,22px)]'
                              )}
                            >
                              {column.render
                                ? column.render(row[column.key], row, idx)
                                : String(row[column.key] ?? '-')}
                            </div>
                          </div>
                        ))}
                        {detail && (
                          <div className="flex items-center justify-end text-[var(--text-content-sub-title)]">
                            <ChevronDown
                              size={16}
                              aria-hidden="true"
                              className={cn(
                                'transition-transform duration-200',
                                isOpen && 'rotate-180'
                              )}
                            />
                          </div>
                        )}
                      </div>

                      {detail && isOpen && (
                        <div className="px-4 pb-4 pt-1 bg-[var(--background-card-elevated)] border-b border-[var(--border-default)]">
                          {detail}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination && (
          <div className="flex flex-col items-stretch gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-body-xs text-[var(--text-content-sub-title,#6C756D)]">
                Showing{' '}
                {pagination.total === 0
                  ? 0
                  : (pagination.page - 1) * pagination.pageSize + 1}{' '}
                to{' '}
                {Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.total
                )}{' '}
                of {pagination.total} results
              </p>
              {onPageSizeChange && (
                <select
                  value={pagination.pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="text-body-xs border border-[var(--border-default)] rounded-md px-2 py-1 bg-[var(--background-card)] text-[var(--text-content-title)] focus:outline-none focus:border-[var(--border-active)] cursor-pointer"
                >
                  {pageSizeOptions.map((s) => (
                    <option key={s} value={s}>{s} / page</option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex items-center gap-2 justify-center md:justify-end">
              <TeeButton
                onClick={handlePrevious}
                disabled={pagination.page === 1}
                title="Previous page"
                aria-label="Previous page"
                className="p-2 rounded-md border border-[var(--border-default)] hover:bg-[var(--background-card-elevated)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconChevronLeft size={16} />
              </TeeButton>

              <div className="flex items-center gap-1">
                {getPageWindow(pagination.page, pagination.totalPages).map(
                  (item) =>
                    item === 'ellipsis-start' || item === 'ellipsis-end' ? (
                      <span
                        key={item}
                        className="min-w-8 h-8 flex items-center justify-center text-body-xs text-[var(--text-content-sub-title)]"
                      >
                        …
                      </span>
                    ) : (
                      <TeeButton
                        key={item}
                        onClick={() => onPageChange?.(item)}
                        className={cn(
                          'min-w-8 h-8 rounded-md text-body-xs font-medium transition-colors',
                          pagination.page === item
                            ? 'bg-[var(--background-card)] border border-[var(--border-active)] text-[var(--color-forest)]'
                            : 'border border-[var(--border-default)] hover:bg-[var(--background-card-elevated)]'
                        )}
                      >
                        {item}
                      </TeeButton>
                    )
                )}
              </div>

              <TeeButton
                onClick={handleNext}
                disabled={pagination.page === pagination.totalPages}
                title="Next page"
                aria-label="Next page"
                className="p-2 rounded-md border border-[var(--border-default)] hover:bg-[var(--background-card-elevated)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconChevronRight size={16} />
              </TeeButton>
            </div>
          </div>
        )}
      </div>
    );
  }
);

TeeTable.displayName = 'TeeTable';

export default TeeTable;
